import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import qs from 'qs';
import { Card, CardBody, Col, Row } from 'reactstrap';
import AutorenewIcon from 'mdi-react/AutorenewIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { getToken } from '../../../services/auth';
import {
  aggregateSnapshotRows,
  BRAZIL_STATES,
  getSeverityFill,
  getStateByCode,
  getStateByUf
} from './BrazilStatusMap.utils';
import { ReactComponent as BrazilMapSvg } from './br.svg';

const formatNumber = (value) =>
  new Intl.NumberFormat('pt-BR').format(Number(value || 0));

const formatGroupName = (group) => {
  switch (group) {
    case 'VALIDADO':
      return 'Validado';
    case 'INVALIDO':
      return 'Inválido';
    case 'NA FILA':
      return 'Na fila';
    case 'FREEZER':
      return 'Aguardando';
    case 'COLETANDO':
      return 'Coletando';
    case 'SEM_STATUS':
      return 'Sem status';
    default:
      return group;
  }
};

const emptySummary = {
  total: 0,
  statuses: [],
  errors: [],
  severity: 0
};

const BrazilStatusMap = ({
  inicialDate,
  finalDate,
  selectedStateCode,
  onSelectState
}) => {
  const [refresh, setRefresh] = useState(false);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState([]);
  const [previewUf, setPreviewUf] = useState(null);
  const mapRef = useRef(null);

  const selectedState = getStateByCode(selectedStateCode);
  const selectedUf = selectedState?.uf || null;
  const statesBySvgId = useMemo(
    () => BRAZIL_STATES.reduce((acc, state) => {
      acc[`BR${state.uf}`] = state;
      return acc;
    }, {}),
    []
  );

  const snapshot = useMemo(() => aggregateSnapshotRows(rows), [rows]);
  const activeUf = previewUf || selectedUf;
  const activeState = activeUf ? getStateByUf(activeUf) : null;
  const activeSummary = activeUf
    ? snapshot.byUf[activeUf] || { ...emptySummary, uf: activeUf }
    : snapshot.national || emptySummary;

  const getSnapshot = useCallback(() => {
    const { CancelToken } = axios;
    const source = CancelToken.source();

    setRefresh(true);
    setError(false);

    axios
      .get(`${process.env.REACT_APP_WAS_KEY}/notas/status-snapshot`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        cancelToken: source.token,
        params: { inicialDate, finalDate, state: 'ALL' },
        paramsSerializer(params) {
          return qs.stringify(params, { encode: false });
        }
      })
      .then((res) => {
        setRows(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setRows([]);
          setError(true);
        }
      })
      .finally(() => setRefresh(false));

    return () => source.cancel();
  }, [inicialDate, finalDate]);

  useEffect(() => getSnapshot(), [getSnapshot]);

  useEffect(() => {
    if (!mapRef.current) return;

    BRAZIL_STATES.forEach((state) => {
      const path = mapRef.current.querySelector(`#features path#BR${state.uf}`);
      const summary = snapshot.byUf[state.uf];
      if (!path) return;

      path.setAttribute('role', 'button');
      path.setAttribute('tabindex', '0');
      path.setAttribute(
        'aria-label',
        `${state.name}: ${formatNumber(summary?.total)} notas`
      );
      path.style.fill = getSeverityFill(summary?.severity || 0, !!summary?.total);
      path.classList.toggle(
        'dashboard__brazil-map-state--selected',
        selectedUf === state.uf
      );
      path.classList.toggle(
        'dashboard__brazil-map-state--preview',
        previewUf === state.uf
      );
    });
  }, [previewUf, selectedUf, snapshot]);

  const getStateFromEvent = (event) => {
    const target = event.target.closest
      ? event.target.closest('#features path[id^="BR"]')
      : null;

    return target ? statesBySvgId[target.id] : null;
  };

  const handleMapMouseOver = (event) => {
    const state = getStateFromEvent(event);
    if (state) setPreviewUf(state.uf);
  };

  const handleMapClick = (event) => {
    const state = getStateFromEvent(event);
    if (state) onSelectState(state.code);
  };

  const handleStateKeyDown = (event) => {
    const state = getStateFromEvent(event);
    if (!state) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectState(state.code);
    }
  };

  return (
    <Card>
      <CardBody className="panel__body dashboard__brazil-map-card">
        {refresh ? (
          <div className="panel__refresh">
            <LoadingIcon />
          </div>
        ) : (
          ''
        )}
        <div className="panel__btns">
          <button
            className="panel__btn"
            aria-label="Atualizar mapa de status"
            type="button"
            onClick={getSnapshot}
          >
            <AutorenewIcon />
          </button>
        </div>
        <div className="panel__title">
          <h5 className="bold-text">Status das notas por estado</h5>
        </div>

        <Row className="dashboard__brazil-map-content">
          <Col md={7}>
            <div
              ref={mapRef}
              className="dashboard__brazil-map-wrap"
              role="presentation"
              onClick={handleMapClick}
              onMouseOver={handleMapMouseOver}
              onMouseLeave={() => setPreviewUf(null)}
              onFocus={(event) => {
                const state = getStateFromEvent(event);
                if (state) setPreviewUf(state.uf);
              }}
              onBlur={() => setPreviewUf(null)}
              onKeyDown={handleStateKeyDown}
            >
              <BrazilMapSvg
                className="dashboard__brazil-map"
                aria-label="Mapa do Brasil por status de notas"
                role="img"
              />
            </div>
          </Col>
          <Col md={5}>
            <div className="dashboard__brazil-map-details">
              <div className="dashboard__brazil-map-heading">
                <div>
                  <p className="dashboard__brazil-map-eyebrow">
                    {activeState ? activeState.uf : 'BR'}
                  </p>
                  <h4>{activeState ? activeState.name : 'Todos os estados'}</h4>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => onSelectState('ALL')}
                  disabled={!selectedUf}
                >
                  Todos
                </button>
              </div>

              {error ? (
                <p className="text-content m-0">
                  Não foi possível carregar o status dos estados.
                </p>
              ) : (
                <>
                  <div className="dashboard__brazil-map-total">
                    <span>Total de notas</span>
                    <strong>{formatNumber(activeSummary.total)}</strong>
                  </div>

                  {activeSummary.total > 0 ? (
                    <>
                      <div className="dashboard__brazil-map-section">
                        <h6>Status</h6>
                        <ul>
                          {activeSummary.statuses.map((status) => (
                            <li key={status.name}>
                              <span>{formatGroupName(status.name)}</span>
                              <strong>{formatNumber(status.value)}</strong>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="dashboard__brazil-map-section">
                        <h6>Principais erros</h6>
                        {activeSummary.errors.length > 0 ? (
                          <ul>
                            {activeSummary.errors.map((item) => (
                              <li key={item.message}>
                                <span title={item.message}>{item.message}</span>
                                <strong>{formatNumber(item.value)}</strong>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-content m-0">
                            Nenhum erro encontrado nesse período.
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-content m-0">
                      Não foi encontrado nenhum dado nesse periodo.
                    </p>
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

BrazilStatusMap.propTypes = {
  finalDate: PropTypes.string.isRequired,
  inicialDate: PropTypes.string.isRequired,
  selectedStateCode: PropTypes.string,
  onSelectState: PropTypes.func.isRequired
};

BrazilStatusMap.defaultProps = {
  selectedStateCode: 'ALL'
};

export default BrazilStatusMap;
